import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid-pro";

import { GHGTarget } from "./data/GHGTarget";
import MultiSelect from "./MultiSelect";
import {
  _addTargetForm,
  _updateTargetForm,
} from "middlewares/TargetformApi/targetform";
import Spinner from "components/Spinner";
import { DataTable } from "components/DataTable";
import { Scope3GHGReductionModel } from "./data/Scope3GHGReduction";
import { TitleDefinitionConstants } from "constants/constant";
import Alerts from "components/Alerts";
import numericValidator from "services/numericValidator";
import {
  scopesCoveredOptions,
  targetYearsAfter,
} from "./data/TargetFormData";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import Blurb from "components/Blurb/Blurb";
import RequiredLabelV2 from "./RequiredLabelV2";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

// Define the props interface
interface AddTargetFormProps {
  handleCloseSnackBar: (state: AlertsProps) => void;
  hideDialogHandler: () => void;
  getCustomGHGTargetsByLocationsId: () => void;
  defaultGHGTarget: GHGTarget;
  initialCategories: Scope3GHGReductionModel[];
  /** Baseline year resolved from the location; undefined when the location has none. */
  resolvedBaselineYear?: number;
}

/**
 * Component for adding or updating a GHG target form.
 * @param {AddTargetFormProps} props - The props for the component.
 * @returns {JSX.Element} AddTargetForm component.
 */
const AddTargetForm = (props: AddTargetFormProps) => {
  const isPositiveReduction = (reduction: number | string | undefined) => {
    const value = parseFloat((reduction ?? "0").toString());
    return !Number.isNaN(value) && value > 0;
  };

  const getReducedCategories = (categories: Scope3GHGReductionModel[]) =>
    categories.filter((category) => isPositiveReduction(category.reduction));

  const getReducedCategoryIds = (categories: Scope3GHGReductionModel[]) =>
    getReducedCategories(categories).map((category) => category.catagory);

  // Extract location ID from the route parameters
  const { LocationId } = useParams();
  const parsedLocationId = Number(LocationId);

  const initialTargetForm: GHGTarget = {
    ...props.defaultGHGTarget,
    locationID: parsedLocationId,
  };

  // // State variables
  // Refs and state for Select FormControls
  const targetYearRef = useRef<HTMLDivElement>(null);
  const [targetForm, setTargetForm] = useState<GHGTarget>(initialTargetForm);
  const [hasBaselineScope3GHG, setHasBaselineScope3GHG] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isScopesCovered, setIsScopesCovered] = useState(false);
  const [isScopes3Covered, setIsScopes3Covered] = useState(false);
  const [isYearValid, setIsYearValid] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState<number[]>([]);
  const [categoryselectedIds, setCategoryselectedIds] = useState(
    [] as Array<number>
  );
  const [updatedCategories, setUpdatedCategories] = useState<
    Scope3GHGReductionModel[]
  >(props.initialCategories);

  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm();

  const hasBaselineYear = props.resolvedBaselineYear != null;
  const validTargetYears = targetYearsAfter(props.resolvedBaselineYear);
  const storedTargetYear = Number(targetForm.targetYear);
  const invalidStoredTargetYear =
    Number.isFinite(storedTargetYear) && !validTargetYears.includes(storedTargetYear)
      ? storedTargetYear
      : undefined;
  const displayedTargetYears =
    invalidStoredTargetYear == null
      ? validTargetYears
      : [...validTargetYears, invalidStoredTargetYear].sort((a, b) => a - b);
  // Existing targets keep their stored base year on screen so the change is visible rather
  // than silently applied.
  const baseYearDiverges =
    hasBaselineYear &&
    targetForm.targetNumber > 0 &&
    targetForm.baseYear != null &&
    Number(targetForm.baseYear) !== props.resolvedBaselineYear;

  const CategoryColumns: GridColDef[] = [
    {
      field: "catagory",
      headerName: "Category",
      width: 300,
      disableColumnMenu: true,
    },
    {
      field: "catagoryName",
      headerName: "Category Name",
      width: 350,
      disableColumnMenu: true,
    },
    {
      field: "reduction",
      headerName: "% Reduction",
      width: 350,
      description: TitleDefinitionConstants.ScopeReduction,
      renderCell: (params) => (
        <FormControl fullWidth>
          <TextField
            value={params.row.reduction ?? 0}
            name={`reduction-${params.row.nzdmId}`}
            inputProps={{
              "data-testid": "reduction",
              inputMode: "numeric",
              maxLength: 5,
              style: { fontSize: 12, padding: '4px 6px', height: 30 }
            }}
            size="small"
            variant="outlined"
            placeholder="% Reduction"
            onChange={(e) =>
              handleReductionChange(params.row.nzdmId, e.target.value)
            }
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </FormControl>
      ),
      disableColumnMenu: true,
    },
  ];

  /**
   * Updates categories from the target form.
   */

  const updateCategoriesFromTargetForm = () => {
    const updatedCategoriesCopy = [...updatedCategories];
    const newCategoryselectedIds: number[] = [];

    updatedCategoriesCopy.forEach((category) => {
      const matchingScope = targetForm.scope3GHGReduction?.find(
        (scope) => scope.catagory === category.catagory
      );

      if (matchingScope) {
        // Update the values for matching categories
        category.reduction = matchingScope.reduction;
        category.selected = isPositiveReduction(matchingScope.reduction);
        category.id = matchingScope.id;
        if (isPositiveReduction(matchingScope.reduction)) {
          newCategoryselectedIds.push(category.catagory);
        }
      } else {
        // No match found, set reduction to zero
        category.reduction = "0";
        category.selected = false;
      }
    });

    setUpdatedCategories(updatedCategoriesCopy);
    setCategoryselectedIds(newCategoryselectedIds);
    setHasBaselineScope3GHG(targetForm?.scopesCovered.includes("3"));
  };

  // Effect to update selected scopes and categories when the targetForm changes
  useEffect(() => {
    const selectedOptions = targetForm.scopesCovered
      ? targetForm.scopesCovered.split(",").map((scope) => parseInt(scope, 10))
      : [];

    setSelectedScopes(selectedOptions);

    const initialTargetForm: GHGTarget = {
      ...props.defaultGHGTarget,
      locationID: parsedLocationId,
    };

    setTargetForm(initialTargetForm);

    register("scope1Reduction");
    setValue("scope1Reduction", targetForm.scope1Reduction);

    register("scope2Reduction");
    setValue("scope2Reduction", targetForm.scope2Reduction);

    const initialCategoriesCopy = props.initialCategories.map((category) => ({
      ...category,
      reduction: "0",
      id: 0,
      selected: false,
    }));

    setUpdatedCategories(initialCategoriesCopy);

    setCategoryselectedIds([]);
    if (targetForm.targetNumber > 0 && targetForm.scopesCovered.includes("3")) {
      updateCategoriesFromTargetForm();
    }
  }, [props.defaultGHGTarget, props.initialCategories]);

  //change events

  const handleTextChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
      | SelectChangeEvent<number>
  ) => {
    setTargetForm({
      ...targetForm,
      [event.target.name]: event.target.value,
    });
  };

  const handleScopeTextChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
      | SelectChangeEvent<number>
  ) => {
    setTargetForm({
      ...targetForm,
      [event.target.name]: numericValidator.allowNumericValue(
        event.target.value.toString()
      ),
    });
  };

  const handleSelectionChange = (selectedOptions: number[]) => {
    const hasScope3Selected = selectedOptions.includes(3);
    setHasBaselineScope3GHG(hasScope3Selected);

    const selectedOptionsString = selectedOptions.join(", ");

    setTargetForm({
      ...targetForm,
      scopesCovered: selectedOptionsString,
      scope3GHGReduction: hasScope3Selected
        ? targetForm.scope3GHGReduction
        : null,
    });

    if (!hasScope3Selected) {
      const resetCategories = updatedCategories.map((category) => ({
        ...category,
        reduction: "0",
        selected: false,
      }));
      setUpdatedCategories(resetCategories);
      setCategoryselectedIds([]);
      setCheckboxChecked(false);
      setTextFieldValue("");
      setIsScopes3Covered(false);
    }

    setSelectedScopes(selectedOptions);
    setIsScopesCovered(false);
  };

  const handleReductionChange = (id: number, value: string) => {
    setUpdatedCategories((prevCategories) => {
      const updatedCategoriesCopy = prevCategories.map((category) => {
        if (category.nzdmId === id) {
          const parsedReduction = numericValidator.allowNumericValue(value);
          return {
            ...category,
            reduction: parsedReduction,
            selected: isPositiveReduction(parsedReduction),
          };
        }
        return category;
      });

      const selectedCategories = getReducedCategories(updatedCategoriesCopy);
      const selectedCategoryIds = getReducedCategoryIds(updatedCategoriesCopy);
      setCategoryselectedIds(selectedCategoryIds);

      if (selectedCategories.length > 0) {
        setTargetForm((prevTargetForm) => ({
          ...prevTargetForm,
          scope3GHGReduction: selectedCategories,
        }));
      } else {
        setTargetForm((prevTargetForm) => ({
          ...prevTargetForm,
          scope3GHGReduction: null,
        }));
      }
      return updatedCategoriesCopy;
    });
  };

  const handleCheckboxChange = (event: any) => {
    const isChecked = event.target.checked;
    setCheckboxChecked(isChecked);
    if (isChecked) {
      updateAllCategories(textFieldValue);
    } else {
      resetAllCategories();
    }
  };

  const handleTextFieldChange = (event: any) => {
    const value = event.target.value;
    setTextFieldValue(value);
    if (checkboxChecked) {
      updateAllCategories(value);
    }
  };

  const updateAllCategories = (value: any) => {
    const updatedCategoriesCopy = updatedCategories.map((category) => ({
      ...category,
      reduction: numericValidator.allowNumericValue(value),
      selected: isPositiveReduction(value),
    }));
    const selectedCategories = getReducedCategories(updatedCategoriesCopy);
    setUpdatedCategories(updatedCategoriesCopy);
    setCategoryselectedIds(getReducedCategoryIds(updatedCategoriesCopy));
    setTargetForm((prevTargetForm) => ({
      ...prevTargetForm,
      scope3GHGReduction:
        selectedCategories.length > 0 ? selectedCategories : null,
    }));
  };

  const resetAllCategories = () => {
    const updatedCategoriesCopy = updatedCategories.map((category) => ({
      ...category,
      reduction: "0",
      selected: false,
    }));
    setUpdatedCategories(updatedCategoriesCopy);
    setCategoryselectedIds([]);
    setTextFieldValue("");
    setTargetForm((prevTargetForm) => ({
      ...prevTargetForm,
      scope3GHGReduction: null,
    }));
  };

  // Save the form data

  const handleSaveOrUpdate = (apiFunction: Function) => {
    if (typeof apiFunction === "function") {
      setLoading(true);
      setFormErrorMessage(null);
      // The API resolves the base year from the location, so sending one back would only
      // re-submit the stale stored value.
      const { baseYear, ...payload } = targetForm;
      apiFunction(payload)
        .then((res: GHGTarget) => {
          if (res) {
            setLoading(false);
            const message =
              targetForm.targetNumber === 0
                ? `${targetForm.targetName} Created Successfully !`
                : "Data Saved Successfully !";
            props.getCustomGHGTargetsByLocationsId();
            props.handleCloseSnackBar({
              show: true,
              severity: "success",
              message: message,
            });
            props.hideDialogHandler();
          }
        })
        .catch((err: any) => {
          const message = getFriendlyErrorMessage(
            err,
            "We could not save the target form right now. Please try again."
          );

          setFormErrorMessage(message);
          reportUiError(err, {
            feature: "TargetForm",
            action: targetForm.targetNumber > 0 ? "update target" : "add target",
            component: "AddTargetForm",
            route: typeof window !== "undefined" ? window.location.pathname : undefined,
            extra: {
              locationId: parsedLocationId,
              targetNumber: targetForm.targetNumber,
            },
          });
          setLoading(false);
        });
    } else {
      const message = "Target form could not be submitted due to an internal configuration error.";
      setFormErrorMessage(message);
      reportUiError(new Error(message), {
        feature: "TargetForm",
        action: "save target",
        component: "AddTargetForm",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
    }
  };

  const onSave = () => {
    let isValid = true;
    // Validate against the baseline the calculation will actually use, not the stored base year.
    if (
      props.resolvedBaselineYear == null ||
      targetForm.targetYear <= props.resolvedBaselineYear
    ) {
      isValid = false;
      setIsYearValid(true);
    } else {
      setIsYearValid(false);
    }
    if (targetForm.scopesCovered?.length === 0) {
      setIsScopesCovered(true);
      isValid = false;
    } else {
      setIsScopesCovered(false);
    }

    if (
      targetForm.scopesCovered?.includes("3") &&
      (!targetForm.scope3GHGReduction || targetForm.scope3GHGReduction.length === 0)
    ) {
      setIsScopes3Covered(true);
      isValid = false;
    } else {
      setIsScopes3Covered(false);
    }

    if (isValid) {
      if (targetForm.targetNumber > 0) {
        handleSaveOrUpdate(_updateTargetForm);
      } else {
        handleSaveOrUpdate(_addTargetForm);
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSave)} data-testid="form">
        <Box sx={{ m: 2 }}>
          {formErrorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="target-form-error-alert">
              {formErrorMessage}
            </Alert>
          )}
          {!hasBaselineYear && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="no-baseline-alert">
              This location has no baseline year and no historical emissions, so a target
              cannot be calculated. Set a base year or add emissions data for the location
              first.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6} sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 600, pb: 1.5, color: "red" }}>
                * Indicates required fields
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ mb: 3 }}>
              {isScopesCovered && (
                <Alerts
                  severity="error"
                  message="At least one must be selected from GHG Scopes Covered"
                  show={true}
                />
              )}
              {isScopes3Covered && (
                <Alerts
                  severity="error"
                  message="At least one must be selected from categories"
                  show={true}
                />
              )}
              {isYearValid && (
                <Alerts
                  severity="error"
                  message="The target year value cannot be less than Base year value"
                  show={true}
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6} sx={{ mb: 3 }}>
              <RequiredLabelV2
                labelText="Target Name"
                isHelperTextRequired={false}
                content=""
              />
              <TextField
                {...register("targetName", {
                  required: true,
                })}
                inputProps={{ "data-testid": "targetName", style: { fontSize: 12, padding: '8px 6px 4px 6px', height: 30 } }}
                required
                name="targetName"
                onChange={(e) => {
                  clearErrors("targetName");
                  handleTextChange(e);
                }}
                value={targetForm.targetName}
                size="small"
                fullWidth
                error={!!errors.targetName}
                sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', pt: 0.5, '.MuiInputBase-input': { height: 30, padding: '8px 6px 4px 6px', boxSizing: 'border-box' } }}
              />
            </Grid>
            <Grid item xs={6} sx={{ mb: 3 }}>
              <RequiredLabelV2
                labelText="Baseline Year"
                isHelperTextRequired={true}
                content={TitleDefinitionConstants.BaseYear}
              />
              {/* The baseline year is derived from the location's data, not chosen here: every
                  target is calculated against it, so letting each target pick its own would
                  produce trajectories that cannot be compared. */}
              <Typography
                data-testid="baseYear"
                sx={{
                  fontSize: 12,
                  height: 30,
                  minHeight: 30,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 6px',
                  mt: 0.5,
                  color: hasBaselineYear ? 'inherit' : 'text.secondary',
                }}
              >
                {props.resolvedBaselineYear ?? "No baseline year"}
              </Typography>
              {baseYearDiverges && (
                <Typography
                  data-testid="baseYearDivergence"
                  sx={{ fontSize: 11, color: "#b45a3f", pt: 0.5 }}
                >
                  This target was saved with base year {targetForm.baseYear}. It is now
                  calculated against the location baseline year {props.resolvedBaselineYear}.
                </Typography>
              )}
          </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Target Year"
                isHelperTextRequired={true}
                content={TitleDefinitionConstants.TargetYear}
              />
              <FormControl fullWidth size="small" ref={targetYearRef} sx={{ fontSize: 12, minWidth: 0, ml: 0, pt: 0.5 }}>
                <Select
                  value={targetForm.targetYear}
                  required
                  data-testid="targetYear"
                  onChange={(e) => {
                    handleTextChange(e);
                  }}
                  displayEmpty
                  size="small"
                  fullWidth
                  name="targetYear"
                  sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', mt: '-4px', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', boxSizing: 'border-box' } }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        fontSize: 12,
                        minWidth: targetYearRef.current ? targetYearRef.current.getBoundingClientRect().width : undefined,
                        maxWidth: targetYearRef.current ? targetYearRef.current.getBoundingClientRect().width : undefined,
                      },
                    },
                  }}
                  onOpen={() => {}}
                >
                  <MenuItem value="" sx={{ fontSize: 12 }}>Select Target Year</MenuItem>
                  {displayedTargetYears.map((year) => (
                    <MenuItem
                      key={year}
                      value={year}
                      disabled={year === invalidStoredTargetYear}
                      sx={{ fontSize: 12 }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="GHG Scopes Covered"
                isHelperTextRequired={true}
                content={TitleDefinitionConstants.ScopesCovered}
              />
              <Box sx={{ pt: 0.5 }}>
                <MultiSelect
                  options={scopesCoveredOptions}
                  onSelectionChange={handleSelectionChange}
                  selectedOptions={selectedScopes}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Scope 1 % Reduction"
                isHelperTextRequired={true}
                content={TitleDefinitionConstants.ScopeReduction}
              />
              <TextField
                {...register("scope1Reduction", {
                  validate: (value) => {
                    if (!selectedScopes.includes(1)) {
                      return true;
                    }
                    const floatValue = parseFloat(value);
                    return floatValue >= 0 && floatValue <= 100;
                  },
                })}
                name="scope1Reduction"
                required
                onChange={(e) => {
                  clearErrors("scope1Reduction");
                  handleScopeTextChange(e);
                }}
                value={
                  selectedScopes.includes(1) ? targetForm.scope1Reduction : "0"
                }
                size="small"
                fullWidth
                error={!!errors.scope1Reduction}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 5,
                  "data-testid": "scope1Reduction",
                  style: { fontSize: 12, padding: '4px 6px', height: 30 }
                }}
                disabled={!selectedScopes.includes(1)}
                sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Scope 2 % Reduction"
                isHelperTextRequired={true}
                content={TitleDefinitionConstants.ScopeReduction}
              />
              <TextField
                {...register("scope2Reduction", {
                  validate: (value) => {
                    if (!selectedScopes.includes(2)) {
                      return true;
                    }
                    const floatValue = parseFloat(value);
                    return floatValue >= 0 && floatValue <= 100;
                  },
                })}
                name="scope2Reduction"
                onChange={(e) => {
                  clearErrors("scope2Reduction");
                  handleScopeTextChange(e);
                }}
                value={
                  selectedScopes.includes(2) ? targetForm.scope2Reduction : "0"
                }
                size="small"
                fullWidth
                error={!!errors.scope2Reduction}
                required
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 5,
                  "data-testid": "scope2Reduction",
                  style: { fontSize: 12, padding: '4px 6px', height: 30 }
                }}
                disabled={!selectedScopes.includes(2)}
                sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3, ml: 0.2 }}>
            <Blurb
              infoText={TitleDefinitionConstants.ScopeReductionNote}
              title={"Note:"}
              style={{ fontSize: 7, lineHeight: 1.4, padding: 8 }}
            ></Blurb>
          </Grid>

          {hasBaselineScope3GHG && (
            <>
              <Divider />
              <br />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    data-testid="BaselineScope3GHGEmissions"
                    sx={{ fontWeight: "bold", mb: 1.5, color: "#718096", fontSize: 14, lineHeight: 1.8 }}
                  >
                    Baseline Scope 3 GHG Emissions
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkboxChecked}
                        onChange={handleCheckboxChange}
                        data-testid="allCategory"
                        sx={{ p: 0.5 }}
                      />
                    }
                    label={<span style={{ fontSize: 12 }}>Apply to all categories</span>}
                    sx={{ fontSize: 12, ml: 1 }}
                  />
                  <TextField
                    value={textFieldValue}
                    onChange={handleTextFieldChange}
                    size="small"
                    disabled={!checkboxChecked}
                    placeholder="Enter Reduction Percentage:"
                    inputProps={{
                      "data-testid": `reductionPercentage`,
                      style: { fontSize: 12, padding: '4px 6px', height: 30 }
                    }}
                    sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                  <DataTable
                    columns={CategoryColumns}
                    rows={updatedCategories}
                    hidePagination={true}
                    disableCheckBox={true}
                  />  
                </Grid>
              </Grid>
            </>
          )}

          <Divider />
          <br />
          <Grid
            container
            justifyContent="flex-end"
            style={{ position: "absolute", bottom: "2rem", right: "2rem" }}
          >
            <Button type="submit" variant="contained" data-testid="btnSave">
              Save Target
            </Button>
          </Grid>
          </Box>
        </form>
        {loading && <Spinner size={80} data-testid="spinner" />}
    </>
  );
};

export default AddTargetForm;
